import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import { 
  MessageCircle, Heart, Brain, Shield, Users, CheckCircle, 
  Phone, Mail, Instagram, Facebook, Linkedin, ArrowRight, 
  Download, Star, Sparkles, Target, Calendar, ClipboardCheck, Menu, X
} from 'lucide-react';

const Home = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [showGuideDialog, setShowGuideDialog] = useState(false);
  const [guideFormData, setGuideFormData] = useState({ name: '', email: '' });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Detectar si es dispositivo móvil
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (window.matchMedia && window.matchMedia("(max-width: 768px)").matches);
  };

  // Función para manejar click en WhatsApp
  const handleWhatsAppClick = (e) => {
    e.preventDefault();
    const phoneNumber = '34628778240';
    const message = encodeURIComponent('Hola, me gustaría recibir información sobre las asesorías de crianza de AprendEduca.');
    const url = isMobile() 
      ? `https://wa.me/${phoneNumber}?text=${message}`
      : `https://web.whatsapp.com/send?phone=${phoneNumber}&text=${message}`;
    window.open(url, '_blank');
  };

  const handleCTAClick = (ctaName) => {
    toast({
      title: "¡Gracias por tu interés!",
      description: `Pronto implementaremos la funcionalidad de ${ctaName}`,
    });
  };

  const handleGuideSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "¡Guía enviada!",
      description: "Revisa tu email para descargar la guía gratuita",
    });
    setShowGuideDialog(false);
    setGuideFormData({ name: '', email: '' });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false); // Cerrar menú móvil después de navegar
    }
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-[#FFF5F0] via-[#FFFBF5] to-[#FFE4D9]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md z-50 border-b border-teal-100/50 shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center cursor-pointer flex-shrink-0"
              aria-label="Volver al inicio"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_aprendeduca/artifacts/llzn77be_logo%20aprendeduca%20%281%29.png"
                alt="AprendEduca"
                className="h-10 sm:h-12 w-auto object-contain max-w-full"
              />
            </button>
            
            {/* Menú Desktop */}
            <nav className="hidden md:flex md:gap-6 lg:gap-8 flex-1 justify-center">
              <button onClick={() => scrollToSection('servicios')} className="text-gray-700 hover:text-teal-600 font-medium transition-colors">
                {t('header.services')}
              </button>
              <button onClick={() => scrollToSection('como-funciona')} className="text-gray-700 hover:text-teal-600 font-medium transition-colors">
                {t('header.howItWorks')}
              </button>
              <button onClick={() => scrollToSection('quien-soy')} className="text-gray-700 hover:text-teal-600 font-medium transition-colors">
                {t('header.aboutUs')}
              </button>
              <button onClick={() => scrollToSection('contacto')} className="text-gray-700 hover:text-teal-600 font-medium transition-colors">
                {t('header.contact')}
              </button>
            </nav>

            {/* Selector de Idioma Desktop */}
            <div className="hidden md:flex items-center gap-3">
              <LanguageSelector />
              
              {/* CTA Desktop */}
              <Button 
                onClick={() => scrollToSection('contacto')} 
                className="bg-teal-600 hover:bg-teal-700 text-white shadow-md flex-shrink-0"
              >
                {t('header.contactButton')}
              </Button>
            </div>

            {/* Selector de Idioma + Hamburguesa Móvil */}
            <div className="flex md:hidden items-center gap-2">
              <LanguageSelector />
              
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="md:hidden"
                  aria-label="Abrir menú"
                >
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <SheetHeader>
                  <SheetTitle className="text-left text-teal-700">{t('header.menu')}</SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col gap-4 mt-8">
                  <button 
                    onClick={() => scrollToSection('servicios')} 
                    className="text-left text-lg text-gray-700 hover:text-teal-600 font-medium transition-colors py-2 border-b border-gray-200"
                  >
                    {t('header.services')}
                  </button>
                  <button 
                    onClick={() => scrollToSection('como-funciona')} 
                    className="text-left text-lg text-gray-700 hover:text-teal-600 font-medium transition-colors py-2 border-b border-gray-200"
                  >
                    {t('header.howItWorks')}
                  </button>
                  <button 
                    onClick={() => scrollToSection('quien-soy')} 
                    className="text-left text-lg text-gray-700 hover:text-teal-600 font-medium transition-colors py-2 border-b border-gray-200"
                  >
                    {t('header.aboutUs')}
                  </button>
                  <button 
                    onClick={() => scrollToSection('contacto')} 
                    className="text-left text-lg text-gray-700 hover:text-teal-600 font-medium transition-colors py-2 border-b border-gray-200"
                  >
                    {t('header.contact')}
                  </button>
                  <Button 
                    onClick={() => { scrollToSection('contacto'); setMobileMenuOpen(false); }} 
                    className="mt-4 bg-teal-600 hover:bg-teal-700 text-white w-full"
                  >
                    {t('header.contactButton')}
                  </Button>
                </nav>
              </SheetContent>
            </Sheet>
            </div>
          </div>
        </div>
      </header>

      {/* 1. HERO SECTION */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="space-y-6 lg:space-y-8">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-gray-900 leading-tight">
                {t('hero.title')}
              </h1>
              
              <p className="text-lg sm:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                {t('hero.subtitle')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button 
                  onClick={() => scrollToSection('contacto')} 
                  size="lg" 
                  className="bg-teal-600 hover:bg-teal-700 text-white text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 w-full sm:w-auto"
                >
                  {t('hero.cta')}
                  <ArrowRight className="ml-2 w-5 h-5 flex-shrink-0" />
                </Button>
              </div>
            </div>
            
            <div className="relative order-first lg:order-last">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-200/20 to-pink-200/20 rounded-[2rem] transform rotate-3"></div>
              <img 
                src="https://customer-assets.emergentagent.com/job_aprendeduca/artifacts/xs99j7l1_hero.png" 
                alt="Familia feliz" 
                className="relative rounded-[2rem] shadow-2xl w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover max-w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 2. BLOQUE DE EMPATÍA */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-4">
              {t('empathy.title')}
            </h2>
            <p className="text-lg sm:text-xl text-gray-600">{t('empathy.subtitle')}</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[
              { text: t('empathy.feelings.patience'), color: "from-yellow-50 to-yellow-100/50", border: "border-yellow-200" },
              { text: t('empathy.feelings.limits'), color: "from-pink-50 to-pink-100/50", border: "border-pink-200" },
              { text: t('empathy.feelings.tantrums'), color: "from-blue-50 to-blue-100/50", border: "border-blue-200" },
              { text: t('empathy.feelings.sleep'), color: "from-purple-50 to-purple-100/50", border: "border-purple-200" },
              { text: t('empathy.feelings.doubt'), color: "from-teal-50 to-teal-100/50", border: "border-teal-200" },
              { text: t('empathy.feelings.struggle'), color: "from-orange-50 to-orange-100/50", border: "border-orange-200" },
            ].map((item, index) => (
              <div 
                key={index} 
                className={`bg-gradient-to-br ${item.color} border ${item.border} p-5 sm:p-6 rounded-2xl shadow-sm hover:shadow-md transition-all hover:-translate-y-1`}
              >
                <p className="text-gray-800 text-base sm:text-lg font-medium leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. SECCIÓN DE TRANSFORMACIÓN */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-50/50 to-blue-50/30">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-4">
              {t('transformation.title')}
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Heart, title: t('transformation.benefits.calm.title'), description: t('transformation.benefits.calm.description') },
              { icon: Shield, title: t('transformation.benefits.limits.title'), description: t('transformation.benefits.limits.description') },
              { icon: MessageCircle, title: t('transformation.benefits.communication.title'), description: t('transformation.benefits.communication.description') },
              { icon: Brain, title: t('transformation.benefits.noGuilt.title'), description: t('transformation.benefits.noGuilt.description') },
              { icon: Users, title: t('transformation.benefits.security.title'), description: t('transformation.benefits.security.description') },
            ].map((item, index) => (
              <Card key={index} className="border-none shadow-md hover:shadow-xl transition-all hover:-translate-y-2 bg-white">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-md">
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 4. QUÉ ES APRENDEDUCA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-8">
            {t('about.title')}
          </h2>
          
          <div className="bg-gradient-to-r from-teal-50 to-blue-50 p-8 sm:p-12 rounded-3xl shadow-md">
            <p className="text-xl sm:text-2xl text-gray-800 leading-relaxed mb-4">
              {t('about.description')}
            </p>
            <p className="text-xl sm:text-2xl font-semibold text-teal-700">
              {t('about.tagline')}
            </p>
          </div>
        </div>
      </section>

      {/* 5. SERVICIOS */}
      <section id="servicios" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#FFF5F0] to-[#FFFBF5]">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-4">
              {t('services.title')}
            </h2>
            <p className="text-xl text-gray-600">{t('services.subtitle')}</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Tarjeta 1 */}
            <Card className="border-2 border-gray-200 shadow-lg hover:shadow-2xl transition-all bg-white">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('services.card1.title')}</h3>
                
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{t('services.card1.features.call')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{t('services.card1.features.analysis')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{t('services.card1.features.session')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{t('services.card1.features.plan')}</span>
                  </li>
                </ul>
                
                <div className="text-2xl font-bold text-gray-900 mb-4">{t('services.card1.price')}</div>
                
                <Button 
                  onClick={() => scrollToSection('contacto')} 
                  className="w-full bg-gray-800 hover:bg-gray-900 text-white"
                >
                  {t('services.card1.cta')}
                </Button>
              </CardContent>
            </Card>

            {/* Tarjeta 2 - Destacada */}
            <Card className="border-4 border-teal-500 shadow-2xl transform md:scale-105 bg-white relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <div className="bg-teal-600 text-white text-sm font-bold py-2 px-6 rounded-full shadow-lg">
                  {t('services.card2.popular')}
                </div>
              </div>
              
              <CardContent className="p-8 pt-10">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">{t('services.card2.title')}</h3>
                
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{t('services.card2.features.call')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{t('services.card2.features.sessions')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{t('services.card2.features.initial')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{t('services.card2.features.followup')}</span>
                  </li>
                </ul>
                
                <div className="text-2xl font-bold text-gray-900 mb-4">{t('services.card2.price')}</div>
                
                <Button 
                  onClick={() => scrollToSection('contacto')} 
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white shadow-md"
                >
                  {t('services.card2.cta')}
                </Button>
              </CardContent>
            </Card>

            {/* Tarjeta 3 - Modalidades */}
            <Card className="border-2 border-gray-200 shadow-lg hover:shadow-2xl transition-all bg-gradient-to-br from-blue-50 to-teal-50">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('services.card3.title')}</h3>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0" />
                    <span className="text-gray-700">{t('services.card3.features.online')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0" />
                    <span className="text-gray-700">{t('services.card3.features.inPerson')}</span>
                  </div>
                </div>
                
                <Button 
                  onClick={() => scrollToSection('contacto')} 
                  className="w-full bg-gray-800 hover:bg-gray-900 text-white"
                >
                  {t('services.card3.cta')}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* 6. CÓMO FUNCIONA */}
      <section id="como-funciona" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-4">
              {t('howItWorks.title')}
            </h2>
          </div>
          
          <div className="space-y-6">
            {[
              { step: "01", title: t('howItWorks.steps.step1'), icon: Calendar },
              { step: "02", title: t('howItWorks.steps.step2'), icon: ClipboardCheck },
              { step: "03", title: t('howItWorks.steps.step3'), icon: Target },
              { step: "04", title: t('howItWorks.steps.step4'), icon: Sparkles },
            ].map((item, index) => (
              <div 
                key={index} 
                className="flex flex-col sm:flex-row gap-6 items-start bg-gradient-to-r from-teal-50/50 to-blue-50/30 p-6 sm:p-8 rounded-2xl shadow-sm hover:shadow-md transition-all"
              >
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-md">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{item.title}</h3>
                </div>
                <div className="flex-shrink-0">
                  <item.icon className="w-8 h-8 text-teal-600" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. QUIÉN SOY */}
      <section id="quien-soy" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#FFF5F0] to-[#FFFBF5]">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-200/20 to-pink-200/20 rounded-[2rem] transform -rotate-3"></div>
              <img 
                src="https://customer-assets.emergentagent.com/job_aprendeduca/artifacts/t45pe4y0_laura.jpg" 
                alt="Laura Farré" 
                className="relative rounded-[2rem] shadow-2xl w-full h-[400px] sm:h-[500px] object-cover"
              />
            </div>
            
            <div className="space-y-6 order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-gray-900">
                {t('aboutUs.title')}
              </h2>
              
              <p className="text-xl sm:text-2xl text-gray-800 leading-relaxed">
                {t('aboutUs.description')}
              </p>
              
              <div className="flex items-center gap-4 pt-4 bg-white p-6 rounded-2xl shadow-md">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-md">
                  <Star className="w-8 h-8 text-white" />
                </div>
                <div>
                  <div className="text-3xl font-bold text-teal-700">50+</div>
                  <div className="text-gray-600 font-medium">{t('aboutUs.families')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. VALORES */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-4">
              {t('values.title')}
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-6">
            {[
              { title: t('values.list.respectful') },
              { title: t('values.list.practical') },
              { title: t('values.list.foundations') },
              { title: t('values.list.professionalism') },
            ].map((value, index) => (
              <div 
                key={index} 
                className="bg-gradient-to-br from-teal-50 to-blue-50 p-6 rounded-2xl shadow-sm hover:shadow-md transition-all border-l-4 border-teal-500"
              >
                <h3 className="text-xl font-bold text-gray-900">{value.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. CTA FINAL */}
      <section id="contacto" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto text-center max-w-4xl">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold mb-6 italic">
            {t('finalCta.title')}
          </h2>
          
          <p className="text-xl sm:text-2xl mb-10 opacity-95">
            {t('finalCta.subtitle')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-2xl mx-auto">
            {/* Botón Principal - WhatsApp */}
            <a
              href="#"
              onClick={handleWhatsAppClick}
              className="w-full sm:w-auto bg-white text-teal-700 hover:bg-gray-100 font-semibold text-lg px-8 py-4 rounded-lg shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 inline-flex items-center justify-center cursor-pointer"
            >
              {t('finalCta.whatsapp')}
            </a>
            
            {/* Botón Secundario - Email */}
            <a
              href={`mailto:fslaura.educa@gmail.com?subject=${encodeURIComponent('Contacto desde la web de AprendEduca')}&body=${encodeURIComponent('Hola, he visitado la web de AprendEduca y me gustaría recibir información sobre las asesorías de crianza.')}`}
              className="w-full sm:w-auto bg-transparent border-2 border-white text-white hover:bg-white hover:text-teal-700 font-medium text-lg px-8 py-4 rounded-lg transition-all inline-flex items-center justify-center"
            >
              {t('finalCta.email')}
            </a>
            
            {/* CTA Terciario - Llamar */}
            <a
              href="tel:+34628778240"
              className="text-white hover:text-gray-200 font-medium text-lg underline underline-offset-4 transition-colors inline-flex items-center justify-center"
            >
              {t('finalCta.call')}
            </a>
          </div>
        </div>
      </section>

      {/* 10. FOOTER */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_aprendeduca/artifacts/7pta5psa_aprendeducalogoredondo%20%281%29.png"
                  alt="AprendEduca"
                  className="w-10 h-10 rounded-full object-cover"
                />
                <span className="text-2xl font-serif font-bold">AprendEduca</span>
              </div>
              <p className="text-gray-400">{t('footer.tagline')}</p>
            </div>
            
            <div>
              <h3 className="font-bold text-lg mb-4">{t('footer.contactTitle')}</h3>
              <div className="space-y-3">
                <a href="mailto:aprendeduca.info@gmail.com" className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors">
                  <Mail className="w-5 h-5" />
                  aprendeduca.info@gmail.com
                </a>
                <a href="tel:+34628778240" className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors">
                  <Phone className="w-5 h-5" />
                  +34 628778240
                </a>
              </div>
            </div>
            
            <div>
              <h3 className="font-bold text-lg mb-4">{t('footer.followTitle')}</h3>
              <div className="flex gap-4">
                <a 
                  href="https://instagram.com" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-12 h-12 bg-gray-800 hover:bg-teal-600 rounded-full flex items-center justify-center transition-all hover:-translate-y-1"
                >
                  <Instagram className="w-6 h-6" />
                </a>
                <a 
                  href="https://facebook.com" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-12 h-12 bg-gray-800 hover:bg-teal-600 rounded-full flex items-center justify-center transition-all hover:-translate-y-1"
                >
                  <Facebook className="w-6 h-6" />
                </a>
                <a 
                  href="https://linkedin.com" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-12 h-12 bg-gray-800 hover:bg-teal-600 rounded-full flex items-center justify-center transition-all hover:-translate-y-1"
                >
                  <Linkedin className="w-6 h-6" />
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400">{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a 
        href="#"
        onClick={handleWhatsAppClick}
        className="fixed bottom-6 right-6 w-16 h-16 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-2xl hover:shadow-3xl transition-all z-50 hover:scale-110"
      >
        <MessageCircle className="w-8 h-8 text-white" />
      </a>

      {/* Guide Download Dialog */}
      <Dialog open={showGuideDialog} onOpenChange={setShowGuideDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif">Descarga tu guía gratuita</DialogTitle>
            <DialogDescription>
              Introduce tus datos y te enviaremos la guía a tu email
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleGuideSubmit} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-2">Nombre</label>
              <Input 
                required
                placeholder="Tu nombre" 
                value={guideFormData.name}
                onChange={(e) => setGuideFormData({...guideFormData, name: e.target.value})}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <Input 
                required
                type="email" 
                placeholder="tu@email.com" 
                value={guideFormData.email}
                onChange={(e) => setGuideFormData({...guideFormData, email: e.target.value})}
              />
            </div>
            
            <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700 text-white">
              Descargar guía gratuita
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Home;
